import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { SEO } from '../components/SEO';
import { ArrowLeft, Play } from 'lucide-react';

// Mentor video data - reordered as requested
const mentorVideos = [
  {
    id: 'mette',
    name: 'Mette Skøde Damsgaard',
    title: 'Digital, Product & Change Leader',
    description: 'Video testimonial coming soon.',
    videoId: 'pUD0tEWZAUo',
    thumbnailUrl: 'https://img.youtube.com/vi/pUD0tEWZAUo/maxresdefault.jpg'
  },
  {
    id: 'alana',
    name: 'Alana Gibbs',
    title: 'Product Management Leader',
    description: 'Video testimonial coming soon.',
    videoId: 'UIUSLtV4CQg',
    thumbnailUrl: 'https://img.youtube.com/vi/UIUSLtV4CQg/maxresdefault.jpg'
  },
  {
    id: 'elmira',
    name: 'Elmira Hasanzade',
    title: 'Technology Executive',
    description: 'Video testimonial coming soon.',
    videoId: 'fWMo47YYUs8',
    thumbnailUrl: 'https://img.youtube.com/vi/fWMo47YYUs8/maxresdefault.jpg'
  },
  {
    id: 'pradiksha',
    name: 'Pradiksha Oommen',
    title: 'Practice Lead, Project Management',
    description: 'Video testimonial coming soon.',
    videoId: 'WOJmQpSlFrA',
    thumbnailUrl: 'https://img.youtube.com/vi/WOJmQpSlFrA/maxresdefault.jpg'
  }
];

// Hero Section
const HeroSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-white" data-testid="aasma-hero">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <Link 
            to="/giving-back" 
            className="inline-flex items-center gap-2 text-[#d4782c] font-dm-sans font-medium mb-8 hover:gap-3 transition-all"
          >
            <ArrowLeft size={18} />
            Back to #GivingBack
          </Link>
          
          <span className="inline-block px-3 py-1 bg-[#faf9f7] text-[#d4782c] font-dm-sans font-semibold text-sm mb-4">
            2025
          </span>
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1a1a1a] mb-4">
            AASMA
          </h1>
          <p className="text-xl md:text-2xl text-[#d4782c] font-dm-sans font-medium">
            Australia-Asia Syndicate for Mentorship & Advancement
          </p>
        </div>
      </div>
    </section>
  );
};

// Content Section Component
const ContentSection = ({ heading, children, bgColor = 'white' }) => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className={`py-12 md:py-16 ${bgColor === 'white' ? 'bg-white' : 'bg-[#faf9f7]'}`}>
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-6">
            {heading}
          </h2>
          <div className="space-y-6 text-[#4a4744] leading-relaxed">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
};

// Video Card Component
const VideoCard = ({ mentor, index }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayClick = () => {
    setIsPlaying(true);
  };

  return (
    <div 
      ref={ref}
      className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${index * 100}ms` }}
      data-testid={`mentor-video-${mentor.id}`}
    >
      <div 
        className="relative bg-white border border-[#e5e2dc] overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Orange top bar animation */}
        <div 
          className="absolute top-0 left-0 h-[3px] bg-[#d4782c] transition-all duration-300 z-10"
          style={{ width: isHovered ? '100%' : '0%' }}
        />

        {/* Video Container */}
        <div className="aspect-video bg-[#1a1a1a] relative">
          {isPlaying ? (
            <iframe
              src={`https://www.youtube.com/embed/${mentor.videoId}?autoplay=1&rel=0`}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={`${mentor.name} testimonial`}
            />
          ) : (
            <button 
              onClick={handlePlayClick}
              className="absolute inset-0 w-full h-full group cursor-pointer"
              aria-label={`Play ${mentor.name}'s video`}
            >
              {/* Thumbnail */}
              <img 
                src={mentor.thumbnailUrl}
                alt={`${mentor.name} video thumbnail`}
                className="w-full h-full object-cover"
              />
              {/* Dark overlay on hover */}
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-[#d4782c] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                  <Play size={32} className="text-white ml-1" fill="white" />
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Mentor Info */}
        <div className="p-6">
          <h3 className="font-playfair text-xl font-bold text-[#1a1a1a] mb-1">
            {mentor.name}
          </h3>
          <p className="text-[#d4782c] font-dm-sans font-medium text-sm">
            {mentor.title}
          </p>
        </div>
      </div>
    </div>
  );
};

// Mentors Section
const MentorsSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-12 md:py-16 bg-[#faf9f7]" data-testid="aasma-mentors">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="font-playfair text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-8">
            Hear From Our Mentors
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {mentorVideos.map((mentor, index) => (
            <VideoCard key={mentor.id} mentor={mentor} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div 
          ref={ref}
          className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[#4a4744] mb-6">
            Interested in future #GivingBack initiatives?
          </p>
          <Link 
            to="/giving-back#get-involved"
            className="inline-block bg-[#1a1a1a] text-white px-8 py-4 font-dm-sans font-semibold hover:bg-[#333] transition-colors"
          >
            Get Involved
          </Link>
        </div>
      </div>
    </section>
  );
};

export default function AasmaPage() {
  return (
    <Layout>
      <SEO 
        title="AASMA | #GivingBack 2025"
        path="/giving-back/aasma-2025"
        description="AASMA is a pro bono mentorship program connecting accomplished Australian women leaders with aspiring female entrepreneurs and professionals in Pakistan."
      />
      <HeroSection />
      
      <ContentSection heading="What is AASMA" bgColor="offwhite">
        <p>
          AASMA is a pro bono mentorship program that connects accomplished Australian women in leadership with aspiring female entrepreneurs and professionals in Pakistan. These women are not lacking in talent or ambition, only in access, encouragement, and opportunity.
        </p>
        <p>
          The program pairs each mentor with one or two mentees for a three-month engagement built on shared experience, human connection, and belief.
        </p>
        <p>
          The name AASMA is more than an acronym. It is also the name of Nadir Khan's eldest sister, his very first mentor. She was the first woman in their family to pursue higher education and earn an MBA. But more than her achievements, it was her courage, clarity, and quiet strength that opened doors for others, inside and outside the family. This program is a tribute to her, and to every woman who has helped others rise simply by walking ahead with conviction.
        </p>
      </ContentSection>

      <ContentSection heading="Why Pakistan" bgColor="white">
        <p>
          Pakistan is home to some of the most resilient, driven, and capable women you will encounter. But systemic barriers, economic, geographic, and cultural, often leave them isolated or unsupported in their professional ambitions.
        </p>
        <p>
          The numbers tell part of the story. Only 1% of entrepreneurs in Pakistan are women. Only 5% of women above the age of 15 have bank accounts. Women make up approximately 22.74% of the total labour force.
        </p>
        <p>
          This is not about charity. It is about surfacing potential that is already there. There is a rising generation of female entrepreneurs and working professionals in Pakistan who simply need someone in their corner. Someone who has walked a few steps ahead.
        </p>
        <p>
          Nadir grew up and studied in Pakistan. It is a country that shaped much of who he is today. Giving back to it, especially through initiatives like AASMA, feels not only meaningful but necessary.
        </p>
      </ContentSection>

      <ContentSection heading="How It Works" bgColor="offwhite">
        <p>
          AASMA is designed to be a low-time, high-impact commitment. Mentors are paired with one or two mentees based on alignment of experience and aspiration. Each mentee takes responsibility for scheduling, and the program provides a welcome pack, a code of ethics, and optional conversation tools to support both parties.
        </p>
        <p>
          The inaugural 2025 cohort brought together 10 mentors and 20 mentees across a three-month program.
        </p>
      </ContentSection>

      <MentorsSection />
      <CTASection />
    </Layout>
  );
}
